// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  withStyles,
  Typography,
  Grid,
  FormHelperText,
} from '@material-ui/core';
import { includes, some, isArray, get } from 'lodash';
import queryString from 'query-string';
import { arrayOf, shape, string, func } from 'prop-types';

import { getI18n, getI18nFormattedMessage } from '../../../../../utils/intl';
import {
  AlertMessages,
  ErrorContainer,
  FormTextField,
  FileDropzone,
  FormActionButtons,
  Switch,
} from '../../../../../components';
import {
  setAuthSettings,
  updateAuthScheme,
  saveAuthScheme,
} from '../../../../../actions/authenticationSettings';
import { getFileExtension, hasError } from '../../../../../utils';
import Actions from '../../Actions';
import styles from '../../styles';

export const LdapProviderConfiguration = (props) => {
  const {
    classes,
    errors,
    authDetails,
    uuid,
    notifyMessages,
    tabValue,
    onCancelDetails,
    hasAttributeMappingFields,
    hasDetailsFields,
  } = props;

  const intl = getI18n(useIntl());
  const queryParam = queryString.parse(props.location.search);
  const qParam = get(queryParam, 'authType') ? `?authType=${queryParam.authType}` : '';

  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [ldapHost, setLdapHost] = useState('');
  const [isLdapHost, setIsLdapHost] = useState(false);
  const [ldapPort, setLdapPort] = useState('');
  const [isLdapPort, setIsLdapPort] = useState(false);
  const [baseName, setBaseName] = useState('');
  const [isBaseName, setIsBaseName] = useState(false);
  const [bindName, setBindName] = useState('');
  const [isBindName, setIsBindName] = useState(false);
  const [bindPassword, setBindPassword] = useState('');
  const [isBindPassword, setIsBindPassword] = useState(false);
  const [start, setStart] = useState('');
  const [isStart, setIsStart] = useState(false);
  const [end, setEnd] = useState('');
  const [isEnd, setIsEnd] = useState(false);
  const [effectiveQuery, setEffectiveQuery] = useState('ID-From-Login');
  const [enhancedSecurity, setEnhancedSecurity] = useState(false);
  const [isFileValidation, setIsFileValidation] = useState(false);
  const [fileValidationMessage, setFileValidationMessage] = useState('');
  const [isSslEnabled, setIsSslEnabled] = useState(false);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const FILE_TYPE = '.crt, .cer, .ca-bundle, .p7b, .p7c, .p7s, .pem, .cert';
  const FILE_HELPER_TEXT = intl.getI18nMessage('label.upload.file.help.text', {
    ext: '.crt, .cer, .ca-bundle, .p7b, .p7c, .p7s, .pem, .cert', type : 'certificate', size: '50kb' });
  const MAX_FILE_SIZE = 50*1024;
  const ALLOWED_FILE_EXTENSIONS = ['crt', 'cer', 'ca-bundle', 'p7b', 'p7c', 'p7s', 'pem', 'cert'];

  const [sslFile, setSslFile] = useState({
    files: [],
    acceptFileTypes: FILE_TYPE,
    maxFiles: 1,
    error: false,
    helperText: FILE_HELPER_TEXT,
    certificate: '',
    certificateFileName: '',
  });

  useEffect(() => {
    if(isUnsavedChanges) {
      const hasSave = !hasAttributeMappingFields(authDetails, queryParam) ||
        !hasDetailsFields(authDetails) || hasError(ldapHost, true) ||
        hasError(ldapPort, true) || hasError(baseName, true) ||
        hasError(bindName, true) || hasError(bindPassword, true) ||
        hasError(start, true) || hasError(end, true);
      setIsSaveDisabled(hasSave);
    }
  }, [isUnsavedChanges, ldapHost, ldapPort, baseName, bindName, bindPassword, start, end]);

  useEffect(() => {
    if(tabValue === 'provider-configuration-tab') {
      setLdapHost(get(authDetails, 'ConfigurationParams.hostname'));
      setLdapPort(get(authDetails, 'ConfigurationParams.port'));
      setBaseName(get(authDetails, 'ConfigurationParams.baseDN'));
      setBindName(get(authDetails, 'ConfigurationParams.bindDN'));
      setStart(get(authDetails, 'ConfigurationParams.ldapQueryStart'));
      setEnd(get(authDetails, 'ConfigurationParams.ldapQueryEnd'));
      const cert = get(authDetails, 'ConfigurationParams.certificate');
        if(cert) {
          sslFile.files = [];
          const file1 = new File([
            new Blob([cert]),
          ], get(authDetails, 'ConfigurationParams.certificateFileName'));
          sslFile.files.push(file1);
          setSslFile({ ...sslFile, ...{
            certificateFileName: get(authDetails, 'ConfigurationParams.certificateFileName'),
            certificate: cert,
          } });
      }
      const query = 'ID-From-Login'+get(authDetails, 'ConfigurationParams.ldapQueryStart')+
        get(authDetails, 'ConfigurationParams.ldapQueryEnd');
      setEffectiveQuery(query);
      setIsSslEnabled(get(authDetails, 'ConfigurationParams.sslEnabled') === 'no' ? false : true);
      setEnhancedSecurity(get(authDetails, 'advancedConfigurations.enhancedPasswordSecurity') === 'no' ? false : true);
    }
  }, [authDetails, tabValue]);

  const clearErrorMessages = () => {
    setSslFile({ ...sslFile, error: false });
    setIsFileValidation(false);
    notifyMessages('', '');
  }

  const hasValidateFile = (file) => {
    const extension = getFileExtension(file.name, ALLOWED_FILE_EXTENSIONS);
    if (!includes(ALLOWED_FILE_EXTENSIONS, extension)) {
      sslFile.error = true;
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      sslFile.error = true;
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('label.error.upload.file.size'));
      return false;
    }
    if (some(sslFile.files, { name: file.name })) {
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('error.gateway.bundle.file.name.unique'));
      return false;
    }
    if (sslFile.files.length === 1) {
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('label.error.auth.file.count'));
      return false;
    }
    setIsFileValidation(false);
    return true;
  };

  const handleChange = (fieldName, value) => {
    switch (fieldName) {
      case 'ldapHost': {
        const ldapHostError = hasError(value, true);
        setLdapHost(value);
        setIsLdapHost(ldapHostError);
        break;
      }
      case 'ldapPort': {
        const ldapPortError = hasError(value, true);
        setLdapPort(value);
        setIsLdapPort(ldapPortError);
        break;
      }
      case 'baseName': {
        const baseNameError = hasError(value, true);
        setBaseName(value);
        setIsBaseName(baseNameError);
        break;
      }
      case 'bindName': {
        const bindNameError = hasError(value, true);
        setBindName(value);
        setIsBindName(bindNameError);
        break;
      }
      case 'bindPassword': {
        const bindPasswordError = hasError(value, true);
        setBindPassword(value);
        setIsBindPassword(bindPasswordError);
        break;
      }
      case 'start': {
        const startError = hasError(value, true);
        setStart(value);
        setIsStart(startError);
        const query = 'ID-From-Login'+value+end;
        setEffectiveQuery(query);
        break;
      }
      case 'end': {
        const endError = hasError(value, true);
        setEnd(value);
        setIsEnd(endError);
        const query = 'ID-From-Login'+start+value;
        setEffectiveQuery(query);
        break;
      }
    }
    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
  }

  const fileLoad = (file) => {
    const reader = new FileReader();
    reader.onabort = () => {
      sslFile.error = true;
      setSslFile({ ...sslFile });
    };
    reader.onerror = () => {
      sslFile.error = true;
      setSslFile({ ...sslFile });
    };
    reader.onload = (e) => {
      if (hasValidateFile(file)) {
        sslFile.error = false;
        const fileContent = reader.result;
        const fileExtenstion = getFileExtension(file.name, ALLOWED_FILE_EXTENSIONS);
        const fileData = file;
        fileData.ext = fileExtenstion;
        fileData.blob = new Blob(
          [fileContent],
          { type: FILE_TYPE },
        );
        sslFile.certificate = e.target.result;
        sslFile.certificateFileName = file.name;
        sslFile.files.push(fileData);
        setSslFile({ ...sslFile });
      } else {
        setSslFile({ ...sslFile });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFiles = (files) => {
    if (isArray(files)) {
      files.forEach((file) => {
        fileLoad(file);
      });
    } else {
      fileLoad(files);
    }
    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
  };

  const onRemoveFile = (file) => {
    clearErrorMessages();
    const remainFiles = sslFile.files.filter((obj) => obj.name !== file.name);
    setSslFile({ ...sslFile, files: remainFiles });
    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
  };

  const onNext = () => {
    const ldapHostError = hasError(ldapHost, true);
    const ldapPortError = hasError(ldapPort, true);
    const baseNameError = hasError(baseName, true);
    const bindNameError = hasError(bindName, true);
    const bindPasswordError = hasError(bindPassword, true);
    const startError = hasError(start, true);
    const endError = hasError(end, true);
    if (some([
      ldapHostError, ldapPortError, baseNameError, bindNameError,
      bindPasswordError, startError, endError,
    ])) {
      setIsLdapHost(ldapHostError);
      setIsLdapPort(ldapPortError);
      setIsBaseName(baseNameError);
      setIsBindName(bindNameError);
      setIsBindPassword(bindPasswordError);
      setIsStart(startError);
      setIsEnd(endError);
    } else {
      setNotificationStatus('');
      setNotificationMessage('');
      setIsFileValidation(false);
      setFileValidationMessage('');
      const enhancedSec = enhancedSecurity ? 'yes' : 'no';
      const configParams = get(sslFile, 'certificateFileName') ?
        {
          baseDN: baseName,
          bindDN:bindName,
          bindPassword: bindPassword,
          hostname: ldapHost,
          ldapQueryEnd: end,
          ldapQueryStart: start,
          port: ldapPort,
          sslEnabled: !isSslEnabled ? 'no' : 'yes',
          certificateFileName: isSslEnabled ? get(sslFile, 'certificateFileName') : '',
          certificate: isSslEnabled ? get(sslFile, 'certificate') : '',
        }
      :
        {
          baseDN: baseName,
          bindDN:bindName,
          bindPassword: bindPassword,
          hostname: ldapHost,
          ldapQueryEnd: end,
          ldapQueryStart: start,
          port: ldapPort,
          sslEnabled: !isSslEnabled ? 'no' : 'yes',
        }

      const data = { ...authDetails, ...{
        advancedConfigurations: {
          enhancedPasswordSecurity: enhancedSec,
        },
        ConfigurationParams: configParams,
        } };

      if (get(authDetails, 'authznType') === 'PORTAL') {
        delete data.RoleMappings;
      }

      if(get(authDetails, 'ConfigurationParams.bindPassword')) {
        if (uuid) {
          props.updateAuthScheme(uuid, data);
        } else {
          props.setAuthSettings(data);
          props.push(`/publish/settings/authentication/add/attribute-mapping${qParam}`);
        }
      } else {
        props.setAuthSettings(data);
        const url = uuid ? `/publish/settings/authentication/edit/${uuid}/attribute-mapping${qParam}`
         : `/publish/settings/authentication/add/attribute-mapping${qParam}`;
        props.push(url);
      }
    }
  }

  const onSave = () => {
    const enhancedSec = enhancedSecurity ? 'yes' : 'no';
      const configParams = get(sslFile, 'certificateFileName') ?
        {
          baseDN: baseName,
          bindDN:bindName,
          bindPassword: bindPassword,
          hostname: ldapHost,
          ldapQueryEnd: end,
          ldapQueryStart: start,
          port: ldapPort,
          sslEnabled: !isSslEnabled ? 'no' : 'yes',
          certificateFileName: isSslEnabled ? get(sslFile, 'certificateFileName') : '',
          certificate: isSslEnabled ? get(sslFile, 'certificate') : '',
        }
      :
        {
          baseDN: baseName,
          bindDN:bindName,
          bindPassword: bindPassword,
          hostname: ldapHost,
          ldapQueryEnd: end,
          ldapQueryStart: start,
          port: ldapPort,
          sslEnabled: !isSslEnabled ? 'no' : 'yes',
        }

      const data = { ...authDetails, ...{
        advancedConfigurations: {
          enhancedPasswordSecurity: enhancedSec,
        },
        ConfigurationParams: configParams,
        } };

      if (get(authDetails, 'authznType') === 'PORTAL') {
        delete data.RoleMappings;
      }
    if (uuid) {
      props.updateAuthScheme(uuid, data);
    } else {
      props.saveAuthScheme(uuid, data);
    }
  }

  const onCancel = () => {
    onCancelDetails(isUnsavedChanges);
  }

  return(
    <div id="auth-settings-provider-container" className={classes.pageContainer}>
      {notificationMessage &&
        <AlertMessages
          id="auth-settings-details-notifications"
          message={notificationMessage}
          variant={notificationStatus}
          onClose={() => {
            setNotificationMessage('');
            setNotificationStatus('');
          }}
        />
      }
      {errors.length > 0 &&
        <ErrorContainer errors={errors} />
      }
      <Grid container>
        <Grid md={10} item>
          <Typography variant="h1" className={classes.pageTitle}>
            {intl.getI18nMessage('label.auth.provider.configuration')}
          </Typography>
        </Grid>
        <Grid md={2} item>
          <div className={classes.deleteContainer}>
            <Actions
              {...props}
              uuid={uuid}
              notifyMessages={notifyMessages}
              disabled={get(authDetails, 'defaultConfig') }
            />
          </div>
        </Grid>
      </Grid>
      <Grid container spacing={3} className={classes.gridContainer}>
        <Grid md={12} item>
          <Typography variant="h4">
            {getI18nFormattedMessage('label.auth.connection.details')}
          </Typography>
          <FormTextField
            id={'auth-settings-pc-ldap-host'}
            name={getI18nFormattedMessage('label.auth.ldap.host')}
            value={ldapHost}
            error={isLdapHost}
            handleChange={(value) => handleChange('ldapHost', value, 255)}
            helperText={intl.getI18nMessage('label.auth.ldap.hostname.help.text')}
          />
          <FormTextField
            id={'auth-settings-pc-ldap-port'}
            name={getI18nFormattedMessage('label.auth.ldap.port')}
            value={ldapPort}
            error={isLdapPort}
            type="number"
            handleChange={(value) => handleChange('ldapPort', value)}
            helperText={getI18nFormattedMessage('label.auth.maximum.length.255')}
          />

          <div className={classes.switchContainer}>
            <Typography variant="h4" className={classes.sslEnabledLabel}>
              {getI18nFormattedMessage('label.auth.ssl.enabled')}
            </Typography>
            <Switch
              id={'auth-settings-pc-ssl-enabled'}
              checked={isSslEnabled}
              onChange={() => {setIsSslEnabled(!isSslEnabled)}}
            />
          </div>
          <hr className={classes.hr}/>
          {isSslEnabled &&
            <FileDropzone
              handleFiles={handleFiles}
              selectedFiles={sslFile}
              labelClass={classes.fileLabel}
              title={intl.getI18nMessage('label.auth.upload.trust.certificate')}
              id={'auth-settings-pc-ssl-upload'}
              allowedFileExtentions={ALLOWED_FILE_EXTENSIONS}
              onRemoveFile={onRemoveFile}
              isValidation={isFileValidation}
              validationMessage={fileValidationMessage}
            />
          }
          <Typography variant="h4">
            {intl.getI18nMessage('label.auth.directory.details')}
          </Typography>
          <FormTextField
            id={'auth-settings-pc-base-name'}
            name={intl.getI18nMessage('label.auth.base.distinguished.name')}
            value={baseName}
            error={isBaseName}
            handleChange={(value) => handleChange('baseName', value, 255)}
            helperText={intl.getI18nMessage('label.auth.base.distinguished.help.text')}
          />
          <FormTextField
            id={'auth-settings-pc-bind-name'}
            name={getI18nFormattedMessage('label.auth.bind.distinguished.name')}
            value={bindName}
            error={isBindName}
            handleChange={(value) => handleChange('bindName', value)}
            helperText={intl.getI18nMessage('label.auth.bind.distinguished.help.text')}
          />
          <FormTextField
            id={'auth-settings-pc-bind-password'}
            name={intl.getI18nMessage('label.auth.bind.password')}
            value={bindPassword}
            error={isBindPassword}
            type="password"
            handleChange={(value) => handleChange('bindPassword', value)}
            helperText={intl.getI18nMessage('label.auth.bind.password.help.text')}
          />

          <Typography variant="h4">
            {getI18nFormattedMessage('label.auth.lookup.query')}
          </Typography>
          <FormHelperText>
            <div className={classes.helperBodyText}>
              {getI18nFormattedMessage('label.auth.lookup.query.help.text')}
            </div>
          </FormHelperText>
          <FormTextField
            id={'auth-settings-pc-start'}
            name={intl.getI18nMessage('label.auth.start')}
            value={start}
            error={isStart}
            handleChange={(value) => handleChange('start', value, 255)}
            helperText={intl.getI18nMessage('label.auth.start.help.text')}
          />
          <FormTextField
            id={'auth-settings-pc-end'}
            name={getI18nFormattedMessage('label.auth.end')}
            value={end}
            error={isEnd}
            handleChange={(value) => handleChange('end', value)}
            helperText={intl.getI18nMessage('label.auth.end.help.text')}
          />
          <FormTextField
            id={'auth-settings-pc-effective-query'}
            name={intl.getI18nMessage('label.auth.effective.query')}
            value={effectiveQuery}
            handleChange={(value) => handleChange('effectiveQuery', value)}
            helperText={intl.getI18nMessage('label.auth.effective.query.help.text')}
            isDisabled={true}
            textFieldClass={classes.disableField}
          />

          <div className={classes.switchContainer}>
            <Typography variant="h4" className={classes.sslEnabledLabel}>
              {intl.getI18nMessage('label.auth.enhanced.security')}
            </Typography>
            <Switch
              id={'auth-settings-pc-enhanced-security'}
              checked={enhancedSecurity}
              onChange={() => {setEnhancedSecurity(!enhancedSecurity)}}
            />
          </div>
          <hr className={classes.hr}/>
          <FormHelperText>
            <div className={classes.helperBodyText}>
              {intl.getI18nMessage('label.auth.enhanced.security.help.text')}
            </div>
          </FormHelperText>
        </Grid>
      </Grid>
      <FormActionButtons
        onNextClick={onNext}
        nextText={getI18nFormattedMessage('label.next.button')}
        showAdditional={true}
        onAdditionalClick={onSave}
        isDisabledAdditional={isSaveDisabled}
        onCancelClick={onCancel}
        id="auth-settings-provider-config-buttons"
      />
    </div>
  );
}

LdapProviderConfiguration.propTypes = {
  classes: shape({}),
  errors: shape({}),
  assets: arrayOf(shape({})),
  authDetails: shape({}),
  uuid: string,
  notifyMessages: func,
  setStateTabs: func,
  setAuthSettings: func,
  tabValue: string,
  saveAuthScheme: func,
  updateAuthScheme: func,
  onCancelDetails: func,
  push: func,
  location: shape({}),
  hasDetailsFields: func,
  hasAttributeMappingFields: func,
};

const mapStateToProps = () => ({
});

const mapDispatchToProps = {
  setAuthSettings,
  saveAuthScheme,
  updateAuthScheme,
};

export default compose(
withStyles(styles),
connect(mapStateToProps, mapDispatchToProps),
)(LdapProviderConfiguration);
